/**
 * 汎用的な同期システムファクトリ関数
 * タグ・備考・追加項目などの同期システムを統一的に生成する
 */

/**
 * PropertySelectionGridとポップアップ間でデータを同期するシステムを作成する
 * CustomEventを使用してdocument上でイベントを発火・リスニングすることで、
 * 異なるコンポーネント間でタグ・備考・追加項目などのデータを双方向に同期する
 *
 * 使用例:
 * ```typescript
 * // タグ同期システムの作成
 * const { dispatch, addListener } = createPropertyGridPopupSyncSystem<string[]>("updateMapClickTags");
 *
 * // グリッドでタグを更新したときにポップアップに通知
 * dispatch(["タグ1", "タグ2"]);
 *
 * // ポップアップ側でリスナーを登録
 * const cleanup = addListener((tags) => {
 *   console.log("タグが更新されました:", tags);
 * });
 * ```
 *
 * @param eventName CustomEventの名前（例: "updateMapClickTags"）
 * @returns dispatch関数（データ更新を通知）とaddListener関数（更新を受け取る）を持つオブジェクト
 */
export const createPropertyGridPopupSyncSystem = <T>(
  eventName: string
): {
  dispatch: (data: T) => void;
  addListener: (callback: (data: T) => void) => () => void;
} => {
  /**
   * 更新イベントを発火する
   * @param data 更新するデータ
   */
  const dispatch = (data: T): void => {
    const event = new CustomEvent(eventName, {
      detail: { data },
    });
    document.dispatchEvent(event);
  };

  /**
   * 更新イベントリスナーを追加する
   * @param callback データが更新された際に呼び出されるコールバック関数
   * @returns クリーンアップ関数
   */
  const addListener = (callback: (data: T) => void): (() => void) => {
    const handleUpdate = (event: CustomEvent<{ data: T }>): void => {
      const newData = event.detail?.data;
      if (newData !== undefined && newData !== null) {
        callback(newData);
      }
    };

    document.addEventListener(eventName, handleUpdate as EventListener);

    return () => {
      document.removeEventListener(eventName, handleUpdate as EventListener);
    };
  };

  return { dispatch, addListener };
};

/**
 * インデックス付きで複数の不動産検索結果を同期するシステムを作成する
 * PropertySelectionGridに複数の検索結果が表示される場合に、
 * 各結果のインデックスを使って特定の検索結果のポップアップとデータを同期する
 *
 * 使用例:
 * ```typescript
 * // 不動産検索用の同期システムの作成
 * const { dispatch, addListener } = createIndexedPropertyGridPopupSyncSystem<string[]>("updateRealEstateSearchTags");
 *
 * // グリッドの0番目の検索結果のタグを更新したときに、その検索結果のポップアップに通知
 * dispatch(0, ["タグ1", "タグ2"]);
 *
 * // 0番目の検索結果のポップアップ側でリスナーを登録（他のインデックスの更新は無視される）
 * const cleanup = addListener(0, (tags) => {
 *   console.log("0番目の検索結果のタグが更新されました:", tags);
 * });
 * ```
 *
 * @param eventName CustomEventの名前（例: "updateRealEstateSearchTags"）
 * @returns dispatch関数（インデックスとデータを指定して更新を通知）とaddListener関数（特定インデックスの更新を受け取る）を持つオブジェクト
 */
export const createIndexedPropertyGridPopupSyncSystem = <T>(
  eventName: string
): {
  dispatch: (index: number, data: T) => void;
  addListener: (targetIndex: number, callback: (data: T) => void) => () => void;
} => {
  /**
   * 更新イベントを発火する
   * @param index 対象のインデックス
   * @param data 更新するデータ
   */
  const dispatch = (index: number, data: T): void => {
    const event = new CustomEvent(eventName, {
      detail: { index, data },
    });
    document.dispatchEvent(event);
  };

  /**
   * 更新イベントリスナーを追加する
   * @param targetIndex リスナーを登録する対象のインデックス
   * @param callback データが更新された際に呼び出されるコールバック関数
   * @returns クリーンアップ関数
   */
  const addListener = (
    targetIndex: number,
    callback: (data: T) => void
  ): (() => void) => {
    const handleUpdate = (
      event: CustomEvent<{ index: number; data: T }>
    ): void => {
      const { index, data } = event.detail || {};
      // 自分のインデックスの更新イベントのみ処理
      if (index === targetIndex && data !== undefined && data !== null) {
        callback(data);
      }
    };

    document.addEventListener(eventName, handleUpdate as EventListener);

    return () => {
      document.removeEventListener(eventName, handleUpdate as EventListener);
    };
  };

  return { dispatch, addListener };
};
